/**
 * F-25 — manual project order inside a stream, the pure half.
 *
 * Mirrors the old static app's `sortProjects(…, 'manual')` + `reorderProject`:
 * the order lives in `project.position` (1..N inside the stream); a project
 * WITHOUT a position displays at the END, in array order. The wire operation is
 * stream-wide — the client always sends the FULL new order
 * (`POST /registry/positions`), so these helpers work on complete id lists.
 *
 * The same machinery drives the two OTHER manual queues (queue «Вручную»):
 *  - a team roster (`teamPosition` on the team's assignments of a project —
 *    one display row per project, dual teammates carry it together);
 *  - a person's own queue (`personPosition` on that person's assignment);
 *  - the WHOLE project list of «Все проекты» (`allPosition`, F-28 — scope-free:
 *    the full registry order, deliberately independent of filters).
 */

import type { Assignment, Person, Project, RegistryDocument } from '../api/types';

/**
 * A manual-order number as a sort key: +Infinity when it is not a finite
 * number — "in the end, in array order" (spec F-25, F-28).
 */
function orderKey(value: number | null): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

/**
 * Sort key of a project in the stream's manual mode: its position.
 */
function positionKey(project: Project): number {
  return orderKey(project.position);
}

/**
 * Manual order is "smallest key wins, missing positions last, ties by array
 * index" — the shape every manual queue below shares. Never mutates the input.
 */
function byPositions<T>(items: readonly T[], keyOf: (item: T) => number): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => keyOf(a.item) - keyOf(b.item) || a.index - b.index)
    .map((entry) => entry.item);
}

/**
 * «Вручную» comparator: position asc; ties (and all-null lists) fall back to
 * the array index, which the explicit index sort below guarantees regardless
 * of `Array.prototype.sort` stability. Never mutates the input.
 */
export function sortProjectsManual(projects: readonly Project[]): Project[] {
  return projects
    .map((project, index) => ({ project, index }))
    .sort((a, b) => positionKey(a.project) - positionKey(b.project) || a.index - b.index)
    .map((entry) => entry.project);
}

/**
 * «Вручную» for the WHOLE project list (F-28, the «Все проекты» view): the
 * same nulls-last / ties-by-array-index rule as {@link sortProjectsManual},
 * keyed on `allPosition`. Display half only — the wire operation always goes
 * through the full unfiltered list (see `withAllPositions`). Never mutates
 * the input.
 */
export function sortProjectsAllManual(projects: readonly Project[]): Project[] {
  return byPositions(projects, (project) => orderKey(project.allPosition));
}

/** The full current order of a (already ordered) list of stream projects. */
export function orderedIds(projects: readonly Project[]): string[] {
  return projects.map((project) => project.id);
}

/** Move the id at `from` to `to`; `null` when the move is a no-op or out of range. */
function moveTo(ids: readonly string[], from: number, to: number): string[] | null {
  if (from < 0 || to < 0 || from >= ids.length || to >= ids.length || from === to) return null;
  const next = [...ids];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as string);
  return next;
}

/**
 * One drag: `dragId` moves onto `targetId`'s place (the old app's
 * `reorderProject`). `null` = nothing to do (same row / id not in the list).
 */
export function reorderIds(ids: readonly string[], dragId: string, targetId: string): string[] | null {
  if (dragId === targetId) return null;
  return moveTo(ids, ids.indexOf(dragId), ids.indexOf(targetId));
}

/**
 * Keyboard alternative: nudge one row one slot up (-1) / down (+1). `null` =
 * the row is already at that edge (or absent).
 */
export function nudgeId(ids: readonly string[], id: string, delta: -1 | 1): string[] | null {
  const from = ids.indexOf(id);
  return moveTo(ids, from, from + delta);
}

/**
 * The «наверх» button of «Все проекты» (F-28): `id` moves to GLOBAL position 1
 * — it means index 0 of the FULL list, filters or not. `null` when the move is
 * a no-op (already first) or the id is absent. Never mutates the input.
 */
export function moveToTop(ids: readonly string[], id: string): string[] | null {
  const from = ids.indexOf(id);
  if (from <= 0) return null;
  return moveTo(ids, from, 0);
}

/**
 * The optimistic cache image: positions from `orderedIds` applied onto the
 * document. Only the stream's own projects are touched; the `version` is left
 * as-is (the server's answer is authoritative — faking a CAS token here would
 * turn a real conflict into silent data loss).
 */
export function withPositions(
  doc: RegistryDocument,
  streamId: string,
  orderedIds: readonly string[],
): RegistryDocument {
  const positionOf = new Map(orderedIds.map((id, index) => [id, index + 1]));
  return {
    ...doc,
    projects: doc.projects.map((project) => {
      const position = positionOf.get(project.id);
      return position !== undefined && project.streamId === streamId
        ? { ...project, position }
        : project;
    }),
  };
}

/**
 * The optimistic cache image of a GLOBAL reorder (F-28): `allPosition` stamped
 * on every listed project — which by contract is every project of the
 * document, no scoping. The `version` stays as-is, as in {@link withPositions}
 * (the server's answer is authoritative).
 */
export function withAllPositions(doc: RegistryDocument, orderedIds: readonly string[]): RegistryDocument {
  const positionOf = new Map(orderedIds.map((id, index) => [id, index + 1]));
  return {
    ...doc,
    projects: doc.projects.map((project) => {
      const position = positionOf.get(project.id);
      return position !== undefined ? { ...project, allPosition: position } : project;
    }),
  };
}

// ---------------------------------------------------------------------------
// Queue «Вручную» for the team roster and the person's own queue
// ---------------------------------------------------------------------------

/** Manual sort key of a TEAM roster row: the best (smallest) teamPosition among the team's own assignments. */
export function teamPositionKey(project: Project, memberIds: ReadonlySet<string>): number {
  const positions = project.assignments
    .filter((assignment) => memberIds.has(assignment.personId))
    .map((assignment) => assignment.teamPosition)
    .filter((position): position is number => typeof position === 'number' && Number.isFinite(position));
  return positions.length > 0 ? Math.min(...positions) : Number.POSITIVE_INFINITY;
}

/** Manual sort key of a PERSON queue row: that person's own assignment position. */
function personPositionKey(project: Project, personId: string): number {
  const position = project.assignments.find((assignment) => assignment.personId === personId)?.personPosition;
  return typeof position === 'number' && Number.isFinite(position) ? position : Number.POSITIVE_INFINITY;
}

/**
 * The team roster in «Вручную»: the team's projects (one row each) ordered by
 * their teamPosition, projects without one last, in array order. Display only —
 * the wire operation is `orderedIds(sortTeamQueueManual(...))` below.
 */
export function sortTeamQueueManual(
  teamId: string,
  projects: readonly Project[],
  people: readonly Person[],
): Project[] {
  const memberIds = new Set(people.filter((person) => person.teamId === teamId).map((person) => person.id));
  const teamProjects = projects.filter((project) => project.assignments.some((a) => memberIds.has(a.personId)));
  return byPositions(teamProjects, (project) => teamPositionKey(project, memberIds));
}

/** The person's queue in «Вручную»: same rule, one assignment per (project, person). */
export function sortPersonQueueManual(personId: string, projects: readonly Project[]): Project[] {
  const own = projects.filter((project) => project.assignments.some((a) => a.personId === personId));
  return byPositions(own, (project) => personPositionKey(project, personId));
}

/**
 * The optimistic cache image of a team reorder: `teamPosition` stamped on ALL
 * of that team's assignments of each listed project (a dual assignment moves as
 * one row). Other teams' assignments — and the version — are left alone, as in
 * {@link withPositions}.
 */
export function withTeamPositions(
  doc: RegistryDocument,
  teamId: string,
  orderedProjectIds: readonly string[],
): RegistryDocument {
  const memberIds = new Set(doc.people.filter((person) => person.teamId === teamId).map((person) => person.id));
  return withAssignmentsPositions(doc, orderedProjectIds, 'teamPosition', (assignment) =>
    memberIds.has(assignment.personId),
  );
}

/** The optimistic cache image of a person's queue reorder (`personPosition`). */
export function withPersonPositions(
  doc: RegistryDocument,
  personId: string,
  orderedProjectIds: readonly string[],
): RegistryDocument {
  return withAssignmentsPositions(doc, orderedProjectIds, 'personPosition', (assignment) =>
    assignment.personId === personId,
  );
}

/** Shared stamping of a manual assignment position — the field is the only difference. */
function withAssignmentsPositions(
  doc: RegistryDocument,
  orderedProjectIds: readonly string[],
  field: 'teamPosition' | 'personPosition',
  owns: (assignment: Assignment) => boolean,
): RegistryDocument {
  const positionOf = new Map(orderedProjectIds.map((id, index) => [id, index + 1]));
  return {
    ...doc,
    projects: doc.projects.map((project) => {
      const position = positionOf.get(project.id);
      if (position === undefined || !project.assignments.some(owns)) return project;
      return {
        ...project,
        assignments: project.assignments.map((assignment) =>
          owns(assignment) ? { ...assignment, [field]: position } : assignment,
        ),
      };
    }),
  };
}
